import * as THREE from 'three';
import { Zombie } from './Zombie';
import { Weapon } from './Weapon';
import { EventEmitter } from '../core/EventEmitter';

export interface HitResult {
  zombie: Zombie;
  point: THREE.Vector3;
  killed: boolean;
}

export class ShootingSystem {
  private raycaster = new THREE.Raycaster();
  private events: EventEmitter;
  private scene: THREE.Scene;
  private isMobile: boolean;

  // Decals
  private decalPool: THREE.Mesh[] = [];
  private decalIndex = 0;
  private readonly MAX_DECALS = ('ontouchstart' in window || navigator.maxTouchPoints > 0) ? 15 : 50;
  private wallMeshes: THREE.Object3D[] = [];

  // Bullet projectiles (Fortnite style)
  private bullets: { group: THREE.Group; velocity: THREE.Vector3; life: number }[] = [];

  // Muzzle flash
  private muzzleFlashLight: THREE.PointLight;
  private muzzleFlashMesh: THREE.Mesh;
  private muzzleFlashTimer = 0;

  constructor(scene: THREE.Scene, events: EventEmitter) {
    this.scene = scene;
    this.events = events;
    this.isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    this.initDecalPool();


    // Pre-create muzzle flash light (reused each shot)
    this.muzzleFlashLight = new THREE.PointLight(0xffaa44, 0, this.isMobile ? 4 : 12);
    this.muzzleFlashLight.decay = 2;
    this.scene.add(this.muzzleFlashLight);

    // Muzzle flash sprite (star-shaped glow)
    const flashGeo = new THREE.PlaneGeometry(0.3, 0.3);
    const flashMat = new THREE.MeshBasicMaterial({
      color: 0xffdd66,
      transparent: true,
      opacity: 0,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    this.muzzleFlashMesh = new THREE.Mesh(flashGeo, flashMat);
    this.muzzleFlashMesh.renderOrder = 999;
    this.scene.add(this.muzzleFlashMesh);
  }

  cacheWallMeshes(): void {
    this.wallMeshes = [];
    this.scene.traverse(obj => {
      if (obj instanceof THREE.Mesh && !obj.userData.isTutorial && !obj.userData.isTarget) {
        this.wallMeshes.push(obj);
      }
    });
  }

  private initDecalPool(): void {
    const geom = new THREE.PlaneGeometry(0.1, 0.1);
    const mat = new THREE.MeshBasicMaterial({
      color: 0x333333,
      transparent: true,
      opacity: 0.8,
      depthWrite: false,
    });
    for (let i = 0; i < this.MAX_DECALS; i++) {
      const decal = new THREE.Mesh(geom, mat);
      decal.visible = false;
      this.scene.add(decal);
      this.decalPool.push(decal);
    }
  }

  shoot(
    camera: THREE.Camera,
    zombies: Zombie[],
    weapon: Weapon
  ): HitResult[] {
    const results: HitResult[] = [];
    const damage = weapon.getDamage();
    const pellets = weapon.config.pellets;
    const spread = weapon.config.spread;
    const range = weapon.config.range;

    // Muzzle position (slightly in front of camera)
    const muzzleOffset = new THREE.Vector3();
    camera.getWorldDirection(muzzleOffset);
    const muzzlePos = camera.position.clone().add(muzzleOffset.clone().multiplyScalar(0.5));

    // Muzzle flash effect
    this.triggerMuzzleFlash(muzzlePos, muzzleOffset);

    // Cache alive zombies outside loop
    const aliveZombies = zombies.filter(z => !z.isDead());
    const meshes = aliveZombies.map(z => z.mesh);
    const wallTargets = this.wallMeshes.length > 0 ? this.wallMeshes : this.scene.children;

    for (let i = 0; i < pellets; i++) {
      const direction = new THREE.Vector3();
      camera.getWorldDirection(direction);

      if (spread > 0) {
        direction.x += (Math.random() - 0.5) * spread;
        direction.y += (Math.random() - 0.5) * spread;
        direction.z += (Math.random() - 0.5) * spread;
        direction.normalize();
      }

      this.raycaster.set(camera.position, direction);
      this.raycaster.far = range;

      const intersects = this.raycaster.intersectObjects(meshes, true);

      if (intersects.length > 0) {
        const hit = intersects[0];
        const hitObject = hit.object;

        const zombie = aliveZombies.find(z => {
          let obj: THREE.Object3D | null = hitObject;
          while (obj) {
            if (obj === z.mesh) return true;
            obj = obj.parent;
          }
          return false;
        });

        if (zombie) {
          // Hit zone detection
          const hitY = hit.point.y;
          const zombieBaseY = zombie.position.y;
          const relativeY = hitY - zombieBaseY;
          const scale = zombie.isBoss ? 1.5 : 1.0;

          // Zone boundaries: legs 0-0.6, belly 0.6-0.9, chest 0.9-1.3, head 1.3+
          let hitZone: string;
          if (relativeY > 1.3 * scale) hitZone = 'head';
          else if (relativeY > 0.9 * scale) hitZone = 'chest';
          else if (relativeY > 0.6 * scale) hitZone = 'belly';
          else hitZone = 'legs';

          // Back detection: check if shot came from behind
          const toZombie = new THREE.Vector3(
            zombie.position.x - camera.position.x,
            0,
            zombie.position.z - camera.position.z
          ).normalize();
          const zombieFacing = new THREE.Vector3(
            Math.sin(zombie.mesh.rotation.y + Math.PI),
            0,
            Math.cos(zombie.mesh.rotation.y + Math.PI)
          ).normalize();
          const dot = toZombie.dot(zombieFacing);
          if (dot > 0.5) hitZone = 'back'; // shot from behind

          // Base damage multiplier (headshot/legshot)
          let dmgMultiplier = 1.0;
          if (hitZone === 'head') dmgMultiplier = 2.0;
          else if (hitZone === 'legs') dmgMultiplier = 0.7;

          // Boss weak point: extra damage if hit matches weak zone
          const wp = zombie.bossWeakPoint;
          if (wp && hitZone === wp.zone) {
            dmgMultiplier = wp.damageMultiplier;
          }

          const finalDamage = Math.round(damage * dmgMultiplier);
          const killed = zombie.takeDamage(finalDamage);
          results.push({ zombie, point: hit.point, killed });
          this.createBloodEffect(hit.point);
          if (hitZone === 'head') {
            this.createHeadshotEffect(hit.point);
          }
          if (wp && hitZone === wp.zone) {
            this.createWeakPointHitEffect(hit.point, wp.glowColor);
          }
          if (killed) {
            this.events.emit('zombie:killed', { zombie, reward: zombie.config.reward });
          }
        }
      } else {
        // Wall/floor hit for decals (use cached wall meshes)
        const allIntersects = this.raycaster.intersectObjects(wallTargets, false);
        if (allIntersects.length > 0 && allIntersects[0].face) {
          this.createBulletDecal(allIntersects[0].point, allIntersects[0].face.normal);
        }
      }

      // Spawn visible projectile (rocket or bullet)
      if (weapon.config.isExplosive) {
        this.spawnRocket(muzzlePos.clone(), direction.clone());
      } else {
        this.spawnBullet(muzzlePos.clone(), direction.clone());
      }
    }

    // Explosive weapons — area damage
    if (weapon.config.isExplosive) {
      let explosionCenter: THREE.Vector3 | null = null;

      if (results.length > 0) {
        explosionCenter = results[0].point;
      } else {
        // Wall/floor hit (use cached walls)
        const dir = new THREE.Vector3();
        camera.getWorldDirection(dir);
        this.raycaster.set(camera.position, dir);
        this.raycaster.far = range;
        const wallHits = this.raycaster.intersectObjects(wallTargets, false);
        if (wallHits.length > 0) {
          explosionCenter = wallHits[0].point;
        }
      }

      if (explosionCenter) {
        this.createExplosionEffect(explosionCenter);

        for (const zombie of zombies.filter(z => !z.isDead())) {
          const dist = zombie.position.distanceTo(explosionCenter);
          if (dist < weapon.config.explosionRadius && !results.find(r => r.zombie === zombie)) {
            const falloff = 1 - (dist / weapon.config.explosionRadius);
            const areaDamage = damage * falloff;
            const killed = zombie.takeDamage(areaDamage);
            results.push({ zombie, point: explosionCenter, killed });
            if (killed) {
              this.events.emit('zombie:killed', { zombie, reward: zombie.config.reward });
            }
          }
        }
      }
    }

    return results;
  }

  private createBloodEffect(point: THREE.Vector3): void {
    const particleCount = this.isMobile ? 3 : 8;
    const geom = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const velocities: THREE.Vector3[] = [];

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = point.x;
      positions[i * 3 + 1] = point.y;
      positions[i * 3 + 2] = point.z;
      velocities.push(new THREE.Vector3(
        (Math.random() - 0.5) * 3,
        Math.random() * 2,
        (Math.random() - 0.5) * 3
      ));
    }

    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.PointsMaterial({
      color: 0x880000,
      size: 0.05,
      transparent: true,
      opacity: 1,
    });

    const particles = new THREE.Points(geom, mat);
    this.scene.add(particles);

    // Animate
    let life = 0;
    const animate = () => {
      life += 0.016;
      const posAttr = geom.getAttribute('position') as THREE.BufferAttribute;
      for (let i = 0; i < particleCount; i++) {
        posAttr.setXYZ(
          i,
          posAttr.getX(i) + velocities[i].x * 0.016,
          posAttr.getY(i) + velocities[i].y * 0.016 - 5 * 0.016 * life,
          posAttr.getZ(i) + velocities[i].z * 0.016
        );
      }
      posAttr.needsUpdate = true;
      mat.opacity = 1 - life * 2;

      if (life < 0.5) {
        requestAnimationFrame(animate);
      } else {
        this.scene.remove(particles);
        geom.dispose();
        mat.dispose();
      }
    };
    animate();
  }

  private createHeadshotEffect(point: THREE.Vector3): void {
    // Extra blood + flash for headshot
    const flashGeo = new THREE.SphereGeometry(0.08, 6, 4);
    const flashMat = new THREE.MeshBasicMaterial({
      color: 0xff0000, transparent: true, opacity: 0.8,
    });
    const flash = new THREE.Mesh(flashGeo, flashMat);
    flash.position.copy(point);
    this.scene.add(flash);

    let life = 0;
    const animate = () => {
      life += 0.02;
      flash.scale.setScalar(1 + life * 4);
      flashMat.opacity = 0.8 - life * 3;
      if (life < 0.3) {
        requestAnimationFrame(animate);
      } else {
        this.scene.remove(flash);
        flashGeo.dispose();
        flashMat.dispose();
      }
    };
    animate();
  }

  private createWeakPointHitEffect(point: THREE.Vector3, color: number): void {
    // Bright colored flash + expanding ring for weak point hit
    const ringGeo = new THREE.RingGeometry(0.05, 0.12, 12);
    const ringMat = new THREE.MeshBasicMaterial({
      color, transparent: true, opacity: 1, side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.position.copy(point);
    ring.lookAt(this.scene.position); // face camera roughly
    this.scene.add(ring);

    let life = 0;
    const animate = () => {
      life += 0.025;
      ring.scale.setScalar(1 + life * 6);
      ringMat.opacity = 1 - life * 3;
      if (life < 0.35) {
        requestAnimationFrame(animate);
      } else {
        this.scene.remove(ring);
        ringGeo.dispose();
        ringMat.dispose();
      }
    };
    animate();
  }

  private createBulletDecal(point: THREE.Vector3, normal: THREE.Vector3): void {
    const decal = this.decalPool[this.decalIndex];
    this.decalIndex = (this.decalIndex + 1) % this.MAX_DECALS;

    decal.position.copy(point).add(normal.clone().multiplyScalar(0.01));
    decal.lookAt(point.clone().add(normal));
    decal.visible = true;
  }

  private createExplosionEffect(point: THREE.Vector3): void {
    // Flash light (skip on mobile)
    let light: THREE.PointLight | null = null;
    if (!this.isMobile) {
      light = new THREE.PointLight(0xff6600, 8, 8);
      light.position.copy(point);
      this.scene.add(light);
    }

    // Explosion sphere (low poly)
    const geom = new THREE.SphereGeometry(0.5, this.isMobile ? 4 : 6, this.isMobile ? 3 : 4);
    const mat = new THREE.MeshBasicMaterial({
      color: 0xff4400,
      transparent: true,
      opacity: 0.8,
    });
    const sphere = new THREE.Mesh(geom, mat);
    sphere.position.copy(point);
    this.scene.add(sphere);

    let life = 0;
    const animate = () => {
      life += 0.02; // faster steps = fewer frames
      const scale = 1 + life * 6;
      sphere.scale.setScalar(scale);
      mat.opacity = 0.8 - life * 2.5;
      if (light) light.intensity = 8 * (1 - life * 3);

      if (life < 0.35) {
        requestAnimationFrame(animate);
      } else {
        this.scene.remove(sphere);
        geom.dispose();
        mat.dispose();
        if (light) { this.scene.remove(light); light.dispose(); }
      }
    };
    animate();
  }

  private spawnBullet(origin: THREE.Vector3, direction: THREE.Vector3): void {
    const group = new THREE.Group();
    group.position.copy(origin);

    // Bullet tip
    const tipGeo = new THREE.SphereGeometry(0.03, 6, 4);
    const tipMat = new THREE.MeshBasicMaterial({ color: 0xffffaa });
    group.add(new THREE.Mesh(tipGeo, tipMat));

    // Trail
    const trailGeo = new THREE.CylinderGeometry(0.015, 0.003, 0.6, 4);
    trailGeo.rotateX(Math.PI / 2);
    const trailMat = new THREE.MeshBasicMaterial({ color: 0xffaa22, transparent: true, opacity: 0.7 });
    const trail = new THREE.Mesh(trailGeo, trailMat);
    trail.position.z = 0.3;
    group.add(trail);

    if (!this.isMobile) {
      const light = new THREE.PointLight(0xffcc44, 1.5, 2);
      group.add(light);
    }

    const target = origin.clone().add(direction);
    group.lookAt(target);
    this.scene.add(group);

    this.bullets.push({ group, velocity: direction.clone().multiplyScalar(90), life: 0 });
  }

  private spawnRocket(origin: THREE.Vector3, direction: THREE.Vector3): void {
    const group = new THREE.Group();
    group.position.copy(origin);

    // Rocket body (olive green cylinder)
    const bodyGeo = new THREE.CylinderGeometry(0.04, 0.04, 0.3, 6);
    bodyGeo.rotateX(Math.PI / 2);
    const bodyMat = new THREE.MeshBasicMaterial({ color: 0x556b2f });
    group.add(new THREE.Mesh(bodyGeo, bodyMat));

    // Warhead (cone tip, darker)
    const headGeo = new THREE.ConeGeometry(0.045, 0.1, 6);
    headGeo.rotateX(-Math.PI / 2);
    const headMat = new THREE.MeshBasicMaterial({ color: 0x8b4513 });
    const head = new THREE.Mesh(headGeo, headMat);
    head.position.z = -0.2;
    group.add(head);

    // Fins (4 stabilizer fins at rear)
    const finMat = new THREE.MeshBasicMaterial({ color: 0x444444 });
    for (let i = 0; i < 4; i++) {
      const finGeo = new THREE.BoxGeometry(0.002, 0.06, 0.06);
      const fin = new THREE.Mesh(finGeo, finMat);
      fin.position.z = 0.15;
      fin.rotation.z = (i / 4) * Math.PI * 2;
      fin.position.x = Math.cos((i / 4) * Math.PI * 2) * 0.03;
      fin.position.y = Math.sin((i / 4) * Math.PI * 2) * 0.03;
      group.add(fin);
    }

    // Exhaust flame (orange-red glow at back)
    const flameGeo = new THREE.ConeGeometry(0.035, 0.25, 6);
    flameGeo.rotateX(Math.PI / 2);
    const flameMat = new THREE.MeshBasicMaterial({ color: 0xff6600, transparent: true, opacity: 0.8 });
    const flame = new THREE.Mesh(flameGeo, flameMat);
    flame.position.z = 0.27;
    group.add(flame);

    // Inner flame (brighter yellow core)
    const innerGeo = new THREE.ConeGeometry(0.02, 0.15, 4);
    innerGeo.rotateX(Math.PI / 2);
    const innerMat = new THREE.MeshBasicMaterial({ color: 0xffcc00, transparent: true, opacity: 0.9 });
    const inner = new THREE.Mesh(innerGeo, innerMat);
    inner.position.z = 0.22;
    group.add(inner);

    // Smoke trail particles (simple stretched sphere)
    const smokeGeo = new THREE.SphereGeometry(0.05, 4, 3);
    const smokeMat = new THREE.MeshBasicMaterial({ color: 0x888888, transparent: true, opacity: 0.3 });
    const smoke = new THREE.Mesh(smokeGeo, smokeMat);
    smoke.position.z = 0.35;
    smoke.scale.set(1, 1, 3);
    group.add(smoke);

    // Bright light on rocket (skip on mobile)
    if (!this.isMobile) {
      const light = new THREE.PointLight(0xff6600, 3, 6);
      group.add(light);
    }

    const target = origin.clone().add(direction);
    group.lookAt(target);
    this.scene.add(group);

    this.bullets.push({ group, velocity: direction.clone().multiplyScalar(40), life: 0 });
  }

  private triggerMuzzleFlash(pos: THREE.Vector3, dir: THREE.Vector3): void {
    // Position flash at muzzle
    const flashPos = pos.clone().add(dir.clone().multiplyScalar(0.3));
    this.muzzleFlashLight.position.copy(flashPos);
    this.muzzleFlashLight.intensity = 8 + Math.random() * 6; // vary intensity
    this.muzzleFlashLight.color.setHex(
      Math.random() > 0.5 ? 0xffaa44 : 0xffcc66
    );

    this.muzzleFlashMesh.position.copy(flashPos);
    this.muzzleFlashMesh.lookAt(pos); // face camera
    const scale = 0.15 + Math.random() * 0.15;
    this.muzzleFlashMesh.scale.set(scale, scale, scale);
    this.muzzleFlashMesh.rotation.z = Math.random() * Math.PI * 2; // random rotation
    (this.muzzleFlashMesh.material as THREE.MeshBasicMaterial).opacity = 1;

    this.muzzleFlashTimer = 0.06; // ~1-2 frames
  }

  update(dt: number): void {
    // Muzzle flash fade
    if (this.muzzleFlashTimer > 0) {
      this.muzzleFlashTimer -= dt;
      if (this.muzzleFlashTimer <= 0) {
        this.muzzleFlashLight.intensity = 0;
        (this.muzzleFlashMesh.material as THREE.MeshBasicMaterial).opacity = 0;
      }
    }

    // Move bullet projectiles
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i];
      b.life += dt;
      b.group.position.addScaledVector(b.velocity, dt);
      // Fade trail as bullet ages
      const trail = b.group.children[1] as THREE.Mesh;
      if (trail && trail.material instanceof THREE.MeshBasicMaterial) {
        trail.material.opacity = 0.7 * (1 - b.life * 1.5);
      }
      if (b.life > 0.6) {
        this.scene.remove(b.group);
        b.group.traverse(c => {
          if (c instanceof THREE.Mesh) { c.geometry.dispose(); (c.material as THREE.Material).dispose(); }
          if (c instanceof THREE.PointLight) c.dispose();
        });
        this.bullets.splice(i, 1);
      }
    }
  }
}
